/**
 * Drawer — a full-height panel from the right edge, for work that needs room
 * but keeps the page behind it in view: the Safety check, a run's changes.
 *
 * Same behaviour as Dialog (useOverlay.ts: Escape, a click on the dimmed page,
 * the focus trap, focus return). Unlike ops/components/Drawer it needs no
 * KeyboardShortcutProvider, which is mounted only in the ops shell.
 *
 * The panel does not slide in with a transform on purpose: a legacy page
 * embedded here (RulesPreflight) opens its own `position: fixed` modals, and a
 * transformed ancestor would pin those to the drawer instead of the viewport.
 */
import { useId, useRef, type ReactNode, type RefObject } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { IconButton } from '../ui'
import { SCRIM_CLASS, stopClickBubbling, toBody, useOverlay } from './useOverlay'

const WIDTH = {
  md: 'max-w-[460px]',
  lg: 'max-w-2xl',
  xl: 'max-w-5xl',
} as const

export type DrawerProps = {
  open: boolean
  /** Escape, the Close button, a click on the dimmed page. The parent sets `open` false. */
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  /** Pinned under the scrolling body. */
  footer?: ReactNode
  width?: keyof typeof WIDTH
  /** false while a request is in flight: Escape, outside clicks and Close do nothing. */
  dismissible?: boolean
  initialFocusRef?: RefObject<HTMLElement | null>
  className?: string
}

export function Drawer({
  open, onClose, title, description, children, footer, width = 'md',
  dismissible = true, initialFocusRef, className,
}: DrawerProps) {
  const panelRef = useRef<HTMLElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  useOverlay(open, panelRef, { onClose, modal: true, dismissible, initialFocus: initialFocusRef })

  if (!open) return null
  return toBody(
    <div className="fixed inset-0 z-50 flex justify-end" onClick={stopClickBubbling}>
      <div aria-hidden className={SCRIM_CLASS} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-edge bg-surface text-ink shadow-pop outline-none',
          WIDTH[width], className,
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-edge px-5 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-[15px] font-semibold leading-snug text-ink">{title}</h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-[13px] text-ink-muted">{description}</p>
            )}
          </div>
          <IconButton aria-label="Close" title="Close (Esc)" onClick={onClose} disabled={!dismissible} className="-mr-1.5 shrink-0">
            <X size={16} aria-hidden />
          </IconButton>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        {footer != null && (
          <footer className="flex items-center justify-end gap-2 border-t border-edge px-5 py-3">{footer}</footer>
        )}
      </aside>
    </div>,
  )
}
